import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import clsx from 'clsx'
import {
  ArrowUpIcon,
  BotMessageSquareIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleHelpIcon,
  RotateCcwIcon,
  XIcon,
} from 'lucide-react'
import { useAppForm, FormField, TextInput, SelectInput } from '@tandem/ui/form'
import Dropdown from '@tandem/ui/Dropdown'
import Table, { TableRegion } from '@tandem/ui/Table'
import $type from '@tandem/ui/type.css'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import CounterpartyModal from '../CounterpartyModal'
import { fmt, type Row } from '../types'
import AssistantReasoning from './-AssistantReasoning'
import { getFilterSummary, getInitialUserText, getLatestAssistantText, getLatestResult } from './-assistant'
import $ from './-Counterparties.css'
import $layout from '@tandem/ui/layout.css'
import $anim from '@tandem/ui/anim.css'
import $form from '@tandem/ui/form.css'
import $badge from '@tandem/ui/badge.css'

export const Route = createFileRoute('/proexpert/')({
  component: CounterpartiesPage
})

let SHORT: Record<string, string> = { sez: 'СЭЗ', iii: 'ИИИ', licrm: 'РПН', egrul: 'ЕГРЮЛ' }

type Sort = { key: string; dir: 'asc' | 'desc' }

function CounterpartiesPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let companyId = company?.company_id
  let enabled = company?.features?.includes('proexpert') ?? false
  let isAdmin = company?.role === 'admin'

  let { data: meta = [] } = useQuery({ ...trpc.proexpert.registries.queryOptions(), enabled, staleTime: Infinity })
  let { data: rows = [], isFetching } = useQuery({ ...trpc.proexpert.counterparty.list.queryOptions(), enabled })
  let { data: syncStatus } = useQuery({ ...trpc.proexpert.status.queryOptions(), enabled, refetchInterval: 5000 })

  let form = useAppForm({
    defaultValues: {
      search: '',
      smartPrompt: '',
      registry: '' as string | undefined,
      presence: 'has',
      changed: '' as string | undefined,
      risky: false
    }
  })
  let search = useStore(form.store, (state) => state.values.search)
  let assistantInput = useStore(form.store, (state) => state.values.smartPrompt)
  let registry = useStore(form.store, (state) => state.values.registry)
  let presence = useStore(form.store, (state) => state.values.presence)
  let changed = useStore(form.store, (state) => state.values.changed)
  let risky = useStore(form.store, (state) => state.values.risky)

  let [isSmartSearch, setIsSmartSearch] = useState(false)
  let [assistantInns, setAssistantInns] = useState<Set<string> | null>(null)
  let [reasoningOpen, setReasoningOpen] = useState(false)
  let [stopped, setStopped] = useState(false)
  let [assistantSessionId, setAssistantSessionId] = useState(() => crypto.randomUUID())
  let lastAppliedToolCall = useRef<string | null>(null)

  let transport = useMemo(() => new DefaultChatTransport({
    api: `${env.API_URL}/assistant/proexpert/grid-filter`,
    credentials: 'include',
    headers: companyId ? { 'x-company-id': String(companyId) } : undefined,
  }), [companyId])
  let {
    messages,
    sendMessage,
    status: assistantStatus,
    stop,
    error: assistantError,
    setMessages,
    regenerate,
    clearError,
  } = useChat({ id: `proexpert-filter-${assistantSessionId}`, transport, experimental_throttle: 80 })
  let assistantBusy = assistantStatus === 'submitted' || assistantStatus === 'streaming'
  let latestResult = useMemo(() => getLatestResult(messages), [messages])
  let assistantSummary = useMemo(() => getFilterSummary(messages), [messages])
  let originalAssistantQuery = useMemo(() => getInitialUserText(messages), [messages])
  let assistantComplete = assistantStatus === 'ready' && Boolean(latestResult) && !assistantError && !stopped
  let assistantQuestion = assistantStatus === 'ready' && !latestResult && !assistantError && !stopped
    ? getLatestAssistantText(messages)
    : ''

  useEffect(() => {
    if (assistantStatus !== 'ready' || !latestResult || lastAppliedToolCall.current === latestResult.toolCallId) return
    lastAppliedToolCall.current = latestResult.toolCallId
    setAssistantInns(new Set(latestResult.inns))
    form.setFieldValue('smartPrompt', assistantSummary)
  }, [assistantStatus, latestResult, assistantSummary, form])

  useEffect(() => {
    if (assistantQuestion) setReasoningOpen(true)
  }, [assistantQuestion])

  useEffect(() => {
    lastAppliedToolCall.current = null
    setAssistantInns(null)
    setReasoningOpen(false)
    setStopped(false)
  }, [companyId])

  let submitAssistant = (text: string, keepReasoningOpen = false) => {
    let prompt = text.trim()
    if (!prompt || assistantBusy) return
    clearError()
    setStopped(false)
    setReasoningOpen(keepReasoningOpen)
    sendMessage({ text: prompt })
  }

  let submitCommand = (event: FormEvent) => {
    event.preventDefault()
    submitAssistant(assistantInput)
    if (assistantInput.trim()) form.setFieldValue('smartPrompt', '')
  }

  let newAssistantQuery = () => {
    if (assistantBusy) stop()
    setMessages([])
    clearError()
    lastAppliedToolCall.current = null
    setStopped(false)
    setReasoningOpen(false)
    setAssistantInns(null)
    form.setFieldValue('smartPrompt', '')
    setAssistantSessionId(crypto.randomUUID())
  }

  let retryAssistant = () => {
    clearError()
    setStopped(false)
    setReasoningOpen(true)
    regenerate()
  }

  let [selected, setSelected] = useState<string | null>(null)
  let [sort, setSort] = useState<Sort>({ key: 'name', dir: 'asc' })
  let toggleSort = (key: string) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    )

  let progress = syncStatus?.progress?.state === 'running' ? syncStatus.progress : null
  let syncing = progress?.phase === 'registries' ? progress.inn : null
  let columnCount = meta.length + 4

  let filtered = useMemo(() => {
    let matched = isSmartSearch
      ? assistantInns ? rows.filter((row) => assistantInns.has(row.inn)) : rows
      : rows
          .filter((row) => {
            let query = search.trim().toLowerCase()
            return !query || row.inn.includes(query) || (row.company_name ?? '').toLowerCase().includes(query)
          })
          .filter((row) => !registry || ((row.registries[registry] ?? 0) > 0) === (presence === 'has'))
          .filter((row) => {
            let cutoff = changed ? Date.now() - Number(changed) * 86_400_000 : 0
            return !cutoff || (row.last_diff_at != null && Date.parse(row.last_diff_at) >= cutoff)
          })
          .filter((row) => !risky || row.risks.length > 0)

    let multiplier = sort.dir === 'asc' ? 1 : -1
    let name = (row: Row) => row.company_name ?? ''
    let timestamp = (row: Row) => Date.parse(row.synced_at ?? row.updated_at ?? '') || 0
    let primary = (a: Row, b: Row) => {
      if (sort.key === 'name') return name(a).localeCompare(name(b), 'ru')
      if (sort.key === 'synced') return timestamp(a) - timestamp(b)
      let registryLabel = sort.key.slice(4)
      return (a.registries[registryLabel] ?? 0) - (b.registries[registryLabel] ?? 0)
    }
    return matched.sort((a, b) => primary(a, b) * multiplier || name(a).localeCompare(name(b), 'ru'))
  }, [rows, search, registry, presence, changed, risky, isSmartSearch, assistantInns, sort])

  if (!enabled) {
    return (
      <div class={$layout.container}>
        <p class={$type.muted}>Раздел недоступен.</p>
      </div>
    )
  }

  return (
    <div class={$.page}>
      <div class={clsx($layout.container, $layout.center, $.controls)}>
        <h3>Контрагенты</h3>

        <div class={clsx($form.row, $.controlRow)}>
          <button
            type='button'
            title='Умный поиск'
            aria-pressed={isSmartSearch}
            class={clsx('plain', $form.floatAction, $type.underline, !isSmartSearch && 'inactive')}
            onClick={() => {
              if (isSmartSearch) newAssistantQuery()
              setIsSmartSearch((value) => !value)
            }}
          >
            <BotMessageSquareIcon style={{ fontSize: '1.25em' }} />
          </button>

          {isSmartSearch ? (
            <form class={$.smartForm} onSubmit={submitCommand}>
              <form.Field name='smartPrompt'>
                {(field) => (
                  <FormField
                    class={$.smartPrompt}
                    field={field}
                    label={assistantComplete && originalAssistantQuery
                      ? <span title={originalAssistantQuery}>{originalAssistantQuery}</span>
                      : undefined}
                  >
                    <TextInput
                      field={field}
                      placeholder='Например: у кого приостановлена лицензия?'
                      aria-label={assistantComplete ? 'Описание примененного фильтра' : 'Запрос к данным'}
                      disabled={assistantBusy}
                      readOnly={assistantComplete}
                    />
                  </FormField>
                )}
              </form.Field>
              {!assistantComplete && (
                <button
                  type='submit'
                  class={clsx('plain', $form.floatAction)}
                  disabled={!assistantInput.trim() || assistantBusy}
                  title='Выполнить запрос'
                  aria-label='Выполнить запрос'
                >
                  <ArrowUpIcon />
                </button>
              )}
              {(messages.length > 0 || assistantError) && (
                <Dropdown
                  class={$.reasoningDropdown}
                  open={reasoningOpen}
                  onOpenChange={setReasoningOpen}
                  trigger={(
                    <button
                      type='button'
                      class={clsx('plain', $.reasoningTrigger)}
                      title='Показать ход запроса'
                      aria-label='Показать ход запроса'
                      aria-expanded={reasoningOpen}
                      aria-haspopup='dialog'
                    >
                      {assistantBusy ? <Spinner />
                      : assistantQuestion ? <CircleHelpIcon />
                      : assistantError || stopped ? <XIcon />
                      : <CheckIcon />}
                    </button>
                  )}
                >
                  <AssistantReasoning
                    messages={messages}
                    status={assistantStatus}
                    error={assistantError}
                    admin={isAdmin}
                    stopped={stopped}
                    onStop={() => {
                      stop()
                      setStopped(true)
                    }}
                    onRetry={retryAssistant}
                    onReply={(text) => submitAssistant(text, true)}
                  />
                </Dropdown>
              )}
              {assistantInns && <span class={clsx($type.small, $type.muted)}>Найдено: {assistantInns.size}</span>}
              {assistantComplete && (
                <button
                  type='button'
                  class={clsx('plain', $form.floatAction, $.resetButton)}
                  onClick={newAssistantQuery}
                  title='Сбросить запрос'
                  aria-label='Сбросить запрос'
                >
                  <RotateCcwIcon />
                </button>
              )}
            </form>
          ) : (
            <>
              <form.Field name='search'>
                {(field) => (
                  <FormField field={field} label='Поиск по названию или ИНН'>
                    <TextInput field={field} />
                  </FormField>
                )}
              </form.Field>
            <form.Field name='registry'>
              {(field) => (
                <FormField field={field} label='Реестр'>
                  <SelectInput field={field} options={meta.map((item) => ({ value: item.label, label: item.name }))} />
                </FormField>
              )}
            </form.Field>
            {registry && (
              <form.Field name='presence'>
                {(field) => (
                  <FormField field={field} label='Наличие'>
                    <SelectInput
                      field={field}
                      showEmpty={false}
                      options={[
                        { value: 'has', label: 'есть в реестре' },
                        { value: 'none', label: 'нет в реестре' }
                      ]}
                    />
                  </FormField>
                )}
              </form.Field>
            )}
            <form.Field name='changed'>
              {(field) => (
                <FormField field={field} label='Изменения'>
                  <SelectInput
                    field={field}
                    options={[
                      { value: '7', label: 'за неделю' },
                      { value: '30', label: 'за месяц' },
                      { value: '90', label: 'за 3 месяца' }
                    ]}
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name='risky'>
              {(field) => (
                <label class={$.riskCheck}>
                  <input
                    type='checkbox'
                    checked={field.state.value}
                    onChange={(event) => field.handleChange(event.target.checked)}
                  />
                  <span>С рисками</span>
                </label>
              )}
            </form.Field>
            </>
          )}
        </div>
      </div>

      <TableRegion>
        {filtered.length > 0 ? (
          <Table>
              <thead>
                <tr>
                  <SortHeader label='Контрагент' sortKey='name' sort={sort} onSort={toggleSort} />
                  <th>ИНН</th>
                  {meta.map((item) => (
                    <SortHeader
                      key={item.label}
                      label={SHORT[item.label] ?? item.label.toUpperCase()}
                      sortKey={`reg:${item.label}`}
                      title={item.name}
                      class='text-center'
                      sort={sort}
                      onSort={toggleSort}
                    />
                  ))}
                  <th>Риски</th>
                  <SortHeader label='Синхронизирован' sortKey='synced' sort={sort} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.inn}
                    class={clsx($.row, row.inn === selected && $.selected, row.inn === syncing && $anim.pulse)}
                    onClick={() => setSelected(row.inn === selected ? null : row.inn)}
                  >
                    <td title={row.company_name ?? ''}>{row.company_name ?? '—'}</td>
                    <td>{row.inn}</td>
                    {meta.map((item) => {
                      let found = row.registries[item.label]
                      return <td key={item.label} class='text-center'>{found == null ? null : found || ''}</td>
                    })}
                    <td class={$.risks}>
                      <div class={$.riskBadges}>
                        {row.risks.map((risk) => (
                          <span key={`${risk.label}-${risk.status}`} class={clsx($badge.pill, $badge.system)}>
                            <span>{SHORT[risk.label] ?? risk.label.toUpperCase()}</span>
                            <span>{risk.status}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{fmt(row.synced_at ?? row.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={columnCount} class={$.footerTd}>
                    <div class={clsx($.footer, $type.small, $type.muted)}>
                      <div class={$.syncStatus}>
                        {progress ? (
                          <>
                            <Spinner />{' '}
                            {progress.phase === 'freshoffice'
                              ? 'Синхронизация данных с FreshOffice…'
                              : `Синхронизация с реестрами…${progress.i != null ? ` (${progress.i}/${progress.total}).` : ''}`}
                          </>
                        ) : syncStatus?.last_sync_at ? `Синхронизировано ${fmt(syncStatus.last_sync_at)}.` : null}
                      </div>
                      <div class={$.count}>Показано: {filtered.length}</div>
                    </div>
                  </td>
                </tr>
              </tfoot>
          </Table>
        ) : (
          <div class={$.emptyState}>
            {isFetching ? <Spinner /> : (
              <>
                <strong>{assistantInns ? 'В выборке нет контрагентов' : rows.length > 0 ? 'Ничего не найдено' : 'Нет контрагентов'}</strong>
                {assistantInns && <span>Откройте ход запроса, чтобы уточнить условия или изменить формулировку.</span>}
              </>
            )}
          </div>
        )}
      </TableRegion>

      {selected && (
        <CounterpartyModal
          inn={selected}
          meta={meta}
          risks={rows.find((row) => row.inn === selected)?.risks ?? []}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  class: className,
  title
}: {
  label: string
  sortKey: string
  sort: Sort
  onSort: (key: string) => void
  class?: string
  title?: string
}) {
  let active = sort.key === sortKey
  return (
    <th class={clsx(className, $.sortable)} title={title} onClick={() => onSort(sortKey)}>
      {label}
      <span class={$.arrow}>
        {active && (sort.dir === 'asc' ? <ChevronUpIcon size={13} /> : <ChevronDownIcon size={13} />)}
      </span>
    </th>
  )
}
